/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useEffect, useState } from "react"
import { format } from "date-fns"
import { P } from "@cd2/text"

import { titleize } from "@cd2/tools"
import { IRecord } from "../types/schema"

interface AuditEvent {
  id: string
  type: string
  model: string
  recordId: string
  details: {
    field: string
    old_value: any
    new_value: any
  }[]
  createdAt: string
  user: {
    id: string
    name: string
  }
}

export function AuditList({ id }: { id: string }) {
  const skeem = window.skeem
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      fetchAuditLogs()
    }
  }, [id])

  async function fetchAuditLogs() {
    try {
      setLoading(true)
      const events = await skeem.fetch<
        IRecord<"events", "type" | "recordId" | "details" | "model" | "createdAt" | "user">
      >({
        events: {
          attributes: [
            "type",
            "recordId",
            "details",
            "model",
            "createdAt",
            { name: "user", attributes: ["name"] },
          ],
          filter: { matches: { recordId: id, type: { or: ["audit_create", "audit_update"] } } },
          sort: { by: "createdAt", direction: "desc" },
          paginate: { size: 999, page: 1 },
        },
      })

      console.log("Audit logs response", events)

      setEvents(events)
    } catch (error) {
      console.error("Error fetching audit logs", error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div>Loading...</div>
  }

  if (events.length === 0) {
    return <P>No audit history available for this product master.</P>
  }

  return (
    <div>
      <h3>Audit History</h3>
      {events.map(event => (
        <div
          key={event.id}
          css={css`
            margin-bottom: 16px;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            overflow: hidden;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              padding: 12px 16px;
              background-color: #f5f5f5;
              border-bottom: 1px solid #e0e0e0;
            `}
          >
            <div>
              <span
                css={css`
                  display: inline-block;
                  padding: 2px 8px;
                  border-radius: 4px;
                  font-size: 12px;
                  font-weight: 600;
                  background-color: ${event.type === "audit_update"
                    ? "#e3f2fd"
                    : event.type === "audit_create"
                    ? "#e8f5e9"
                    : event.type === "audit_destroy"
                    ? "#ffebee"
                    : "#f5f5f5"};
                  color: ${event.type === "audit_update"
                    ? "#1565c0"
                    : event.type === "audit_create"
                    ? "#2e7d32"
                    : event.type === "audit_destroy"
                    ? "#c62828"
                    : "#616161"};
                `}
              >
                {event.type === "audit_update"
                  ? "Updated"
                  : event.type === "audit_create"
                  ? "Created"
                  : event.type === "audit_destroy"
                  ? "Deleted"
                  : event.type.charAt(0).toUpperCase() + event.type.slice(1)}
              </span>
              <span
                css={css`
                  margin-left: 8px;
                `}
              >
                by {event.user?.name || "Unknown user"}
              </span>
            </div>
            <div
              css={css`
                font-size: 0.9rem;
                color: #666;
              `}
            >
              {format(new Date(event.createdAt), "MMM d, yyyy 'at' h:mm a")}
            </div>
          </div>
          <div
            css={css`
              padding: 16px;
            `}
          >
            {event.details && event.details.length > 0 ? (
              <div
                css={css`
                  overflow-x: auto;
                `}
              >
                <table
                  css={css`
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 12px;
                    th,
                    td {
                      padding: 8px;
                      text-align: left;
                      border-bottom: 1px solid #e0e0e0;
                    }
                    th {
                      background-color: #f5f5f5;
                    }
                  `}
                >
                  <thead>
                    <tr>
                      <th>Field</th>
                      {event.type === "audit_update" && <th>Previous Value</th>}
                      <th>New Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {event.details.map((change, index) => (
                      <tr key={index}>
                        <td
                          css={css`
                            font-weight: 500;
                            white-space: nowrap;
                          `}
                        >
                          {titleize(change.field)}
                        </td>
                        {event.type === "audit_update" && (
                          <td>{formatFieldValue(change.old_value)}</td>
                        )}
                        <td>{formatFieldValue(change.new_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <P>No details available for this action.</P>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// Format field values for display
function formatFieldValue(value: any): React.ReactNode {
  // Handle arrays
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <span
          css={css`
            color: #888;
            font-style: italic;
          `}
        >
          None
        </span>
      )
    }
    return (
      <ul
        css={css`
          margin: 0;
          padding-left: 20px;
        `}
      >
        {value.map((item, i) => (
          <li key={i}>{String(item)}</li>
        ))}
      </ul>
    )
  }

  // Handle null/undefined
  if (value === null || value === undefined) {
    return (
      <span
        css={css`
          color: #888;
          font-style: italic;
        `}
      >
        None
      </span>
    )
  }

  // Handle boolean values
  if (typeof value === "boolean") {
    return value ? "Yes" : "No"
  }

  // Handle empty strings
  if (value === "") {
    return (
      <span
        css={css`
          color: #888;
          font-style: italic;
        `}
      >
        Empty
      </span>
    )
  }

  // Handle objects (shouldn't happen, but just in case)
  if (typeof value === "object") {
    return <span>{JSON.stringify(value)}</span>
  }

  // Default case
  return <span>{String(value)}</span>
}

// User type for auditing purposes
interface AuditUser {
  id: string
}

// Type for handling the different formats of associations
type AssociationValue<T> = T | { id: string } | null | undefined | ({ id: string } | string)[]

// Convert association to a consistent format for comparison
function normalizeAssociation<T>(value: AssociationValue<T>): string | string[] | null {
  if (!value) {
    return null
  }

  // Handle arrays (has many relationships)
  if (Array.isArray(value)) {
    return value.map(item => {
      if (typeof item === "object" && item !== null && "id" in item) {
        return item.id
      }
      return String(item)
    })
  }

  // Handle single objects
  if (typeof value === "object" && "id" in value) {
    return value.id
  }

  return String(value)
}

// Type for mutation data
interface MutationData {
  [key: string]: any
}

// Type for audit entry
interface AuditEntry {
  table_name: string
  record_id: string
  changed_by_user_id: string
  timestamp: Date
  changes: {
    field: string
    old_value: any
    new_value: any
  }[]
}

/**
 * Performs a mutation while auditing changes
 * @param mutationData - The mutation data to send
 * @param existingRecord - The existing record being modified
 * @param currentUser - The user making the changes
 * @returns Promise with the mutation result
 */
export async function auditedMutate<T extends IRecord<any>>(
  mutationData: MutationData,
  existingRecord: T,
  currentUser: AuditUser,
) {
  const skeem = window.skeem
  // Validate that we're updating the same record
  const updateData = Object.values(mutationData)[0]?.update
  if (!updateData || updateData.id !== existingRecord.id) {
    throw new Error("Update data ID doesn't match existing record ID")
  }

  // Extract the table name from the mutation
  const tableName = Object.keys(mutationData)[0]

  // Create audit entry
  const auditEntry: AuditEntry = {
    table_name: tableName,
    record_id: existingRecord.id,
    changed_by_user_id: currentUser.id,
    timestamp: new Date(),
    changes: [],
  }

  // Helper function to compare arrays
  const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false
    }

    // Sort both arrays (if they contain primitives or objects with IDs)
    const sortedArr1 = [...arr1].sort((a, b) => String(a).localeCompare(String(b)))
    const sortedArr2 = [...arr2].sort((a, b) => String(a).localeCompare(String(b)))

    // Compare the sorted arrays
    return sortedArr1.every((val, i) => String(val) === String(sortedArr2[i]))
  }

  // Track changes by comparing existing record with update data
  for (const [key, value] of Object.entries(updateData)) {
    // Skip id field and undefined values
    if (key === "id" || value === undefined) {
      continue
    }

    // Handle special cases for associations
    if (typeof value === "object" && value !== null) {
      // Handle set operations (e.g., user_planner: { set: data.user_planner?.id })
      if ("set" in value) {
        const oldValue = normalizeAssociation(existingRecord[key as keyof typeof existingRecord])
        const newValue = normalizeAssociation(value.set)

        // Check if values are different, with special handling for arrays
        let hasChanged = false

        if (Array.isArray(oldValue) && Array.isArray(newValue)) {
          hasChanged = !areArraysEqual(oldValue, newValue)
        } else {
          hasChanged = oldValue !== newValue
        }

        if (hasChanged) {
          auditEntry.changes.push({
            field: key,
            old_value: oldValue,
            new_value: newValue,
          })
        }
        continue
      }
    }

    // For primitive values, compare directly
    if ((existingRecord as Record<string, any>)[key] !== value) {
      auditEntry.changes.push({
        field: key,
        old_value: (existingRecord as Record<string, any>)[key],
        new_value: value,
      })
    }
  }

  // Only create audit entry if changes were made
  if (auditEntry.changes.length > 0) {
    try {
      // Perform the original mutation
      const result = await skeem.mutate(mutationData)

      await skeem.mutate({
        events: {
          create: {
            type: `audit_${Object.keys(mutationData[auditEntry.table_name])[0]}`,
            model: auditEntry.table_name,
            recordId: auditEntry.record_id,
            details: auditEntry.changes,
            user: { set: currentUser.id },
          },
        },
      })

      return result
    } catch (error) {
      console.error("Error during audited mutation:", error)
      throw error
    }
  } else {
    // If no changes, just return the original mutation
    return await skeem.mutate(mutationData)
  }
}

// Add this new function below the existing auditedMutate function

/**
 * Performs a creation mutation while auditing the new record
 * @param skeem - The skeem instance to use for mutations
 * @param mutationData - The mutation data to send (create operation)
 * @param currentUser - The user creating the record
 * @returns Promise with the mutation result
 */
export async function auditedCreate<T>(
  skeem: any,
  mutationData: MutationData,
  currentUser: AuditUser,
): Promise<T> {
  // Extract the table name from the mutation
  const tableName = Object.keys(mutationData)[0]
  const createData = mutationData[tableName].create

  try {
    // Perform the original mutation
    const result = await skeem.mutate(mutationData)

    // Get the created record ID
    const recordId = result.id

    // For create operations, we log all non-undefined fields as changes (old_value: null)
    const changes = Object.entries(createData)
      .filter(([_key, value]) => value !== undefined)
      .map(([key, value]) => {
        // Handle special cases for associations
        if (typeof value === "object" && value !== null && "set" in value) {
          return {
            field: key,
            old_value: null,
            new_value: normalizeAssociation(value.set),
          }
        }

        return {
          field: key,
          old_value: null,
          new_value: value,
        }
      })

    // Create audit log entry for the creation
    await skeem.mutate({
      events: {
        create: {
          type: "audit_create",
          model: tableName,
          recordId: recordId,
          details: changes,
          user: { set: currentUser.id },
        },
      },
    })

    return result
  } catch (error) {
    console.error("Error during audited creation:", error)
    throw error
  }
}

/**
 * Records a create audit for a record that already exists.
 *
 * Use this when the create has already been performed elsewhere (e.g. by an
 * external process or a separate mutation) but you still want the equivalent
 * of the `audit_create` event that `auditedCreate` would have produced.
 *
 * @param tableName - The model/table name of the created record
 * @param recordId - The id of the already-created record
 * @param createData - The field values the record was created with
 * @param currentUser - The user credited with the creation
 * @returns Promise with the created audit event mutation result
 */
export async function auditCreateAfterTheFact(
  tableName: string,
  recordId: string,
  createData: MutationData,
  currentUser: AuditUser,
) {
  const skeem = window.skeem
  // Log all non-undefined fields as changes (old_value: null), mirroring auditedCreate
  const changes = Object.entries(createData)
    .filter(([_key, value]) => value !== undefined)
    .map(([key, value]) => {
      // Handle special cases for associations
      if (typeof value === "object" && value !== null && "set" in value) {
        return {
          field: key,
          old_value: null,
          new_value: normalizeAssociation(value.set),
        }
      }

      return {
        field: key,
        old_value: null,
        new_value: value,
      }
    })

  try {
    return await skeem.mutate({
      events: {
        create: {
          type: "audit_create",
          model: tableName,
          recordId,
          details: changes,
          user: { set: currentUser.id },
        },
      },
    })
  } catch (error) {
    console.error("Error recording create audit after the fact:", error)
    throw error
  }
}
